import { useViewport } from 'contexts/viewport';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from 'services/api';
import { Box, Button, Checkbox, IconButton, ResponsiveDialog, Stack, styled, Text } from 'shell/components';

const PushRubricDialog = ({ open, onClose, onSave, form }) => {
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const [checked, setChecked] = useState(true);
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        onClose();
    };

    const canvasRubric = (rubric, setting) => {
        let rows = [];
        if (rubric) {
            rubric.forEach((criterion) => {
                const options = criterion.ratings.map((rating) => {
                    const ratingPoint = rating.points;
                    return {
                        id: `${rating.id}`,
                        title: rating.description || t('common:ui.none', 'None'),
                        description: rating.long_description || t('common:ui.none', 'None'),
                        rate: `${ratingPoint}` || 0,
                    };
                });
                const row = {
                    id: `${criterion.id}`,
                    title: criterion.description || t('common:ui.none', 'None'),
                    description: criterion.long_description || t('common:ui.none', 'None'),
                    options: options.sort((a, b) => b.rate - a.rate),
                };
                rows = [...rows, row];
            });
        }

        return {
            title: setting.title,
            criteria: rows,
            sort: 'desc',
        };
    };

    const handleClickSave = async () => {
        setLoading(true);
        const accessToken = localStorage.getItem('access_token');
        let pushedRubric = null;
        let newAPIData = null;

        if (checked) {
            const pushRequest = await apiService.pushRubric({ accessToken, ...form });
            if (process.env.REACT_APP_KIND === 'CANVAS') {
                pushedRubric = canvasRubric(pushRequest.rubric.criteria, pushRequest.rubric);
                newAPIData = await apiService.getAssignment({
                    course_id: form.api_data.course_id,
                    accessToken,
                    assignment_id: form.api_assignment_id,
                });
            }
            setLoading(false);
        } else {
            pushedRubric = form.rubric;
            newAPIData = form.api_data;
        }
        onSave({ api_data: newAPIData, rubric: pushedRubric });
    };

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <TitleStack spacing={2}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'} weight={'medium'}>
                        {t('rubric.push-lms.sync-rubric', 'Sync Rubric To Your LMS?')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
                <Text weight={'regular'}>
                    {t(
                        'rubric.push-lms.difference-note',
                        'There are differences between the assignment rubric on your LMS and TimelyGrader. If you sync TimelyGrader’s rubric to your LMS, it will replace the existing rubric.'
                    )}
                </Text>
            </TitleStack>
            <CustomDialogContent device={device}>
                <Stack direction={'row'} alignItems={'flex-start'} spacing={1}>
                    <Checkbox checked={checked} onChange={() => setChecked((current) => !current)} />
                    <Stack sx={{ paddingTop: 3 }} spacing={2}>
                        <Text weight={'medium'}>
                            {t('rubric.push-lms.sync-tg-rubric', 'Sync TimelyGrader rubric to LMS')}
                        </Text>
                        <CustomBox>
                            <CustomText weight={'regular'}>
                                <Text component={'span'} color={'inherit'} weight={'medium'}>
                                    {t('common:ui.note', 'NOTE').toUpperCase()}:{' '}
                                </Text>
                                {t(
                                    'rubric.push-lms.difference-warning',
                                    'If you don’t sync this rubric with your LMS, only the overall final grade will be passed back for each student.'
                                )}
                            </CustomText>
                        </CustomBox>
                    </Stack>
                </Stack>
            </CustomDialogContent>
            <BottomStack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                <Button variant={'tertiary'} color={'neutral'} onClick={handleClose}>
                    {t('common:controls.cancel', 'Cancel')}
                </Button>
                <Button loading={loading} loadingGrow={loading} disable={loading} onClick={handleClickSave}>
                    {checked
                        ? t('common:controls.confirm-and-proceed', 'Confirm & Proceed')
                        : t('common:controls.skip-and-proceed', 'Skip & Proceed')}
                </Button>
            </BottomStack>
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '600px',
            height: '332px',
        },
    };
});

const TitleStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(7),
    };
});

const BottomStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(1, 7, 0, 0),
    };
});

const CustomDialogContent = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(0, 7, 7, 7),
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '8px',
        padding: theme.spacing(2, 3),
        background: theme.palette.ensightful.warning[100],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.warning[900],
    };
});

export default PushRubricDialog;
